/**
 * Lifecycle actions for the listing detail header: Re-activate / Republish, Pause, Close and Reopen.
 * A draft is published from the guide's Review step, so it has no action here. (The heads-up that
 * posts on other sites stay up, listing-marketing-hub BR-15a, is the page's, below the whole button row.)
 */

import { Button } from '../../../components/ui/button';
import { isListingExpired } from '../../../components/leasing/ListingPaidPeriodIndicator';
import { formatUsd } from '../../../api/billing';
import type { Listing } from '../../../lib/leasing.api';

// ---------------------------------------------------------------------------
// Section: Lifecycle Actions
// ---------------------------------------------------------------------------

interface LifecycleActionsProps {
  listing: Listing;
  canDecide: boolean;
  onActivate: () => void;
  onPause: () => void;
  onClose: () => void;
  onReopen: () => void;
  isActing: boolean;
  /** The plan's listing fee, shown on Republish (an expired listing needs a new paid period). */
  republishFeeCents: number | null;
}

export function LifecycleActions({
  listing,
  canDecide,
  onActivate,
  onPause,
  onClose,
  onReopen,
  isActing,
  republishFeeCents,
}: LifecycleActionsProps) {
  const canActivate = listing.status === 'paused';
  const canPause = listing.status === 'active';
  const canClose = listing.status !== 'closed' && listing.status !== 'draft' && canDecide;
  const canReopen = listing.status === 'closed' && canDecide;

  if (!canActivate && !canPause && !canClose && !canReopen) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {canActivate && (
        <Button
          type="button"
          variant="success"
          data-testid="listing-activate-button"
          onClick={onActivate}
          disabled={isActing}
        >
          {isListingExpired(listing)
            ? `Republish${republishFeeCents !== null ? ` — ${formatUsd(republishFeeCents)}` : ''}`
            : 'Re-activate'}
        </Button>
      )}
      {canPause && (
        <Button
          type="button"
          variant="outline"
          data-testid="listing-pause-button"
          onClick={onPause}
          disabled={isActing}
        >
          Pause
        </Button>
      )}
      {canClose && (
        <Button
          type="button"
          variant="outline"
          data-testid="listing-close-button"
          onClick={onClose}
          disabled={isActing}
          className="border-destructive text-destructive hover:bg-destructive/10"
        >
          Close Listing
        </Button>
      )}
      {canReopen && (
        <Button
          type="button"
          variant="success"
          data-testid="listing-reopen-button"
          onClick={onReopen}
          disabled={isActing}
        >
          Reopen Listing
        </Button>
      )}
    </div>
  );
}
